import { create } from "zustand";
import { ApiRequestError } from "@web/lib/api.ts";
import { loadSpotifySdk } from "./loadSdk.ts";
import { dropPlaybackTokenCache, getPlaybackToken } from "./token.ts";
import type {
  SpotifyPlayerInstance,
  SpotifyWebPlaybackState,
  SpotifyWebPlaybackTrack,
} from "./spotifySdk.ts";
import type { QueueTrack, RepeatMode } from "./types.ts";

type PlayerStatus = {
  message: string;
  isError: boolean;
};

type PlayerUiState = {
  ready: boolean;
  connected: boolean;
  playing: boolean;
  seeking: boolean;
  shuffle: boolean;
  repeat: RepeatMode;
  volume: number;
  positionMs: number;
  durationMs: number;
  queueIndex: number;
  queue: QueueTrack[];
  /** SDK metadata when available; falls back to queue track in the bar. */
  nowPlaying: {
    name: string;
    artists: string;
    coverUrl: string | null;
  } | null;
  status: PlayerStatus;
};

type PlayerActions = {
  init: () => Promise<void>;
  playTracks: (tracks: QueueTrack[], startTrackId?: string) => Promise<void>;
  togglePlay: () => Promise<void>;
  next: () => Promise<void>;
  previous: () => Promise<void>;
  toggleShuffle: () => void;
  cycleRepeat: () => void;
  setVolume: (volume01: number) => Promise<void>;
  beginSeek: () => void;
  previewSeek: (ratio01: number) => void;
  commitSeek: (ratio01: number) => Promise<void>;
  cancelSeek: () => void;
};

export type PlayerStore = PlayerUiState & PlayerActions;

/** Imperative SDK bits stay off the reactive tree (v0 module pattern). */
let sdkPlayer: SpotifyPlayerInstance | null = null;
let deviceId: string | null = null;
let sdkState: SpotifyWebPlaybackState | null = null;
let stateReceivedAt = 0;
let sourceQueue: QueueTrack[] = [];
let endGuard: string | null = null;
let initPromise: Promise<void> | null = null;
let progressTimer: number | null = null;

class PlaybackHttpError extends Error {
  readonly status: number;
  readonly body: string;

  constructor(status: number, body: string) {
    super(`Spotify playback failed (${status})`);
    this.name = "PlaybackHttpError";
    this.status = status;
    this.body = body;
  }
}

function shuffleCopy<T>(items: T[]): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = result[i];
    const b = result[j];
    if (a === undefined || b === undefined) continue;
    result[i] = b;
    result[j] = a;
  }
  return result;
}

function currentQueueTrack(state: PlayerUiState): QueueTrack | null {
  return state.queueIndex >= 0 ? (state.queue[state.queueIndex] ?? null) : null;
}

function artistsFromSdk(track: SpotifyWebPlaybackTrack | null): string {
  if (!track?.artists?.length) return "";
  return track.artists.map((a) => a.name).filter(Boolean).join(", ");
}

function projectedPosition(): number {
  if (!sdkState) return 0;
  if (sdkState.paused) return sdkState.position || 0;
  return Math.min(
    sdkState.duration || Number.POSITIVE_INFINITY,
    (sdkState.position || 0) + (Date.now() - stateReceivedAt),
  );
}

async function spotifyPlayerApi(
  path: string,
  options: RequestInit = {},
  retry = true,
): Promise<unknown> {
  const token = await getPlaybackToken(!retry);
  const res = await fetch(`https://api.spotify.com/v1${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(options.headers ?? {}),
    },
  });
  if (res.status === 401 && retry) {
    dropPlaybackTokenCache();
    return spotifyPlayerApi(path, options, false);
  }
  if (!res.ok) {
    const body = await res.text();
    throw new PlaybackHttpError(res.status, body);
  }
  if (res.status === 204) return null;
  return res.json() as Promise<unknown>;
}

function setStatus(message: string, isError = false): void {
  usePlayerStore.setState({ status: { message, isError } });
}

function bumpProgressFromSdk(): void {
  const state = usePlayerStore.getState();
  if (state.seeking) return;

  const fallback = currentQueueTrack(state);
  const duration = sdkState?.duration || fallback?.duration_ms || 0;
  const position = projectedPosition();
  const playing = Boolean(sdkState && !sdkState.paused);

  usePlayerStore.setState({
    playing,
    positionMs: position,
    durationMs: duration,
  });

  if (
    playing &&
    duration > 1000 &&
    position >= duration - 350 &&
    endGuard !== `${state.queueIndex}:${sdkState?.track_window?.current_track?.uri ?? ""}`
  ) {
    endGuard = `${state.queueIndex}:${sdkState?.track_window?.current_track?.uri ?? ""}`;
    void handleNaturalEnd();
  }
}

function maybeHandleEnded(
  previousState: SpotifyWebPlaybackState | null,
  nextState: SpotifyWebPlaybackState | null,
): void {
  if (!previousState || previousState.paused || previousState.duration <= 0) return;
  const elapsedSinceState = Date.now() - stateReceivedAt;
  const previousPosition = Math.min(
    previousState.duration,
    (previousState.position || 0) + elapsedSinceState,
  );
  const nextLooksEnded =
    (!nextState && previousPosition >= previousState.duration - 1000) ||
    (nextState !== null &&
      nextState.paused &&
      (nextState.position === 0 ||
        (nextState.position >= previousState.duration - 250 &&
          previousPosition >= previousState.duration - 250)));
  if (!nextLooksEnded) return;

  const key = `${usePlayerStore.getState().queueIndex}:${previousState.track_window?.current_track?.uri ?? ""}`;
  if (endGuard === key) return;
  endGuard = key;
  void handleNaturalEnd();
}

function handleSdkState(nextState: SpotifyWebPlaybackState | null): void {
  const previousState = sdkState;
  maybeHandleEnded(previousState, nextState);
  if (!nextState) {
    sdkState = null;
    bumpProgressFromSdk();
    return;
  }
  sdkState = nextState;
  stateReceivedAt = Date.now();

  const sdkTrack = nextState.track_window.current_track;
  const queue = usePlayerStore.getState().queue;
  let queueIndex = usePlayerStore.getState().queueIndex;
  if (sdkTrack?.uri && queue.length) {
    const match = queue.findIndex((track) => track.uri === sdkTrack.uri);
    if (match >= 0) queueIndex = match;
  }

  const fallback = queue[queueIndex] ?? null;
  const cover =
    sdkTrack?.album?.images?.[0]?.url ??
    fallback?.cover_url ??
    null;

  usePlayerStore.setState({
    queueIndex,
    nowPlaying: sdkTrack
      ? {
          name: sdkTrack.name || "Unknown track",
          artists: artistsFromSdk(sdkTrack) || "Unknown artist",
          coverUrl: cover,
        }
      : fallback
        ? {
            name: fallback.name,
            artists: fallback.artists.map((a) => a.name).join(", ") || "Unknown artist",
            coverUrl: fallback.cover_url,
          }
        : null,
    status: { message: "", isError: false },
  });
  bumpProgressFromSdk();
}

async function ensureReady(): Promise<void> {
  await usePlayerStore.getState().init();
  if (!deviceId || !sdkPlayer) {
    throw new Error("Spotify player is still connecting");
  }
}

async function playIndex(index: number): Promise<void> {
  const { queue } = usePlayerStore.getState();
  if (index < 0 || index >= queue.length) return;
  await ensureReady();
  if (!sdkPlayer || !deviceId) return;

  const track = queue[index];
  if (!track?.uri) throw new Error("This track has no Spotify URI");

  endGuard = null;
  sdkState = null;
  usePlayerStore.setState({
    queueIndex: index,
    nowPlaying: {
      name: track.name,
      artists: track.artists.map((a) => a.name).join(", ") || "Unknown artist",
      coverUrl: track.cover_url,
    },
    positionMs: 0,
    durationMs: track.duration_ms ?? 0,
    playing: false,
  });
  setStatus("Starting playback…");

  await sdkPlayer.activateElement();
  await spotifyPlayerApi(`/me/player/play?device_id=${encodeURIComponent(deviceId)}`, {
    method: "PUT",
    body: JSON.stringify({ uris: [track.uri], position_ms: 0 }),
  });
}

async function handleNaturalEnd(): Promise<void> {
  const { repeat, queueIndex, queue } = usePlayerStore.getState();
  if (repeat === "track") {
    await playIndex(queueIndex);
    return;
  }
  const nextIndex = queueIndex + 1;
  if (nextIndex < queue.length) {
    await playIndex(nextIndex);
  } else if (repeat === "context" && queue.length) {
    await playIndex(0);
  }
}

async function runControl(action: () => Promise<void>): Promise<void> {
  try {
    await action();
  } catch (err) {
    console.error(err);
    const status =
      err instanceof PlaybackHttpError
        ? err.status
        : err instanceof ApiRequestError
          ? err.status
          : 0;
    const body =
      err instanceof PlaybackHttpError
        ? err.body
        : err instanceof Error
          ? err.message
          : "";
    const reconnect =
      status === 403 ||
      /scope|premium|token|authentication/i.test(`${body}`);
    setStatus(
      reconnect
        ? "Playback needs Premium and a reconnect with streaming permissions."
        : err instanceof Error
          ? err.message
          : "Playback failed",
      true,
    );
  }
}

function startProgressTimer(): void {
  if (progressTimer !== null) return;
  progressTimer = window.setInterval(() => bumpProgressFromSdk(), 250);
}

export const usePlayerStore = create<PlayerStore>((set, get) => ({
  ready: false,
  connected: false,
  playing: false,
  seeking: false,
  shuffle: false,
  repeat: "off",
  volume: 0.8,
  positionMs: 0,
  durationMs: 0,
  queueIndex: -1,
  queue: [],
  nowPlaying: null,
  status: { message: "", isError: false },

  init: async () => {
    if (sdkPlayer && deviceId) {
      set({ ready: true, connected: true });
      return;
    }
    if (initPromise) return initPromise;

    initPromise = (async () => {
      setStatus("Connecting Spotify player…");
      try {
        await loadSpotifySdk();
        const Player = window.Spotify?.Player;
        if (!Player) throw new Error("Spotify player SDK did not expose Player");

        sdkPlayer = new Player({
          name: "CoyKoi Web Player",
          volume: get().volume,
          getOAuthToken: (cb) => {
            getPlaybackToken()
              .then(cb)
              .catch((err: unknown) => {
                setStatus(err instanceof Error ? err.message : "Token failed", true);
              });
          },
        });

        sdkPlayer.addListener("ready", ({ device_id }) => {
          deviceId = device_id;
          set({ ready: true, connected: true });
          setStatus("Ready");
          window.setTimeout(() => {
            if (usePlayerStore.getState().status.message === "Ready") {
              setStatus("");
            }
          }, 1500);
        });
        sdkPlayer.addListener("not_ready", () => {
          deviceId = null;
          set({ ready: false, connected: false });
          setStatus("Spotify player went offline", true);
        });
        sdkPlayer.addListener("player_state_changed", handleSdkState);
        sdkPlayer.addListener("initialization_error", ({ message }) =>
          setStatus(message, true),
        );
        sdkPlayer.addListener("authentication_error", () =>
          setStatus("Reconnect Spotify to grant playback permissions.", true),
        );
        sdkPlayer.addListener("account_error", () =>
          setStatus("Spotify Premium is required for web playback.", true),
        );
        sdkPlayer.addListener("playback_error", ({ message }) =>
          setStatus(message || "Spotify playback error", true),
        );

        const connected = await sdkPlayer.connect();
        if (!connected) throw new Error("Spotify player could not connect");
        startProgressTimer();
      } catch (err) {
        initPromise = null;
        sdkPlayer = null;
        setStatus(err instanceof Error ? err.message : "Player init failed", true);
        throw err;
      }
    })();

    return initPromise;
  },

  playTracks: async (tracks, startTrackId) => {
    sourceQueue = tracks.filter((t) => Boolean(t.uri));
    const requested = startTrackId
      ? sourceQueue.findIndex((t) => t.id === startTrackId)
      : 0;
    const selectedIndex = requested >= 0 ? requested : 0;
    if (sourceQueue.length === 0) {
      setStatus("No playable Spotify tracks in this view", true);
      return;
    }

    const shuffleOn = get().shuffle;
    let queue: QueueTrack[];
    let queueIndex: number;
    if (shuffleOn) {
      const selected = sourceQueue[selectedIndex];
      if (!selected) return;
      const rest = sourceQueue.filter((_, i) => i !== selectedIndex);
      queue = [selected, ...shuffleCopy(rest)];
      queueIndex = 0;
    } else {
      queue = [...sourceQueue];
      queueIndex = selectedIndex;
    }

    set({ queue, queueIndex });
    if (sdkPlayer) await sdkPlayer.activateElement().catch(() => undefined);
    await runControl(() => playIndex(queueIndex));
  },

  togglePlay: async () => {
    await runControl(async () => {
      await ensureReady();
      if (!sdkPlayer) return;
      if (!sdkState && get().queueIndex >= 0) {
        await playIndex(get().queueIndex);
        return;
      }
      await sdkPlayer.activateElement();
      await sdkPlayer.togglePlay();
    });
  },

  next: async () => {
    await runControl(async () => {
      const { queue, queueIndex, repeat } = get();
      if (!queue.length) return;
      const nextIndex = queueIndex + 1;
      if (nextIndex < queue.length) await playIndex(nextIndex);
      else if (repeat === "context") await playIndex(0);
    });
  },

  previous: async () => {
    await runControl(async () => {
      await ensureReady();
      if (!sdkPlayer) return;
      const { queue, queueIndex, repeat } = get();
      if (!queue.length) return;
      if (projectedPosition() > 3000) {
        await sdkPlayer.seek(0);
        return;
      }
      if (queueIndex > 0) await playIndex(queueIndex - 1);
      else if (repeat === "context") await playIndex(queue.length - 1);
      else await sdkPlayer.seek(0);
    });
  },

  toggleShuffle: () => {
    const current = currentQueueTrack(get());
    const shuffle = !get().shuffle;
    if (current && sourceQueue.length) {
      if (shuffle) {
        const rest = sourceQueue.filter((t) => t.id !== current.id);
        set({ shuffle, queue: [current, ...shuffleCopy(rest)], queueIndex: 0 });
      } else {
        const index = sourceQueue.findIndex((t) => t.id === current.id);
        set({
          shuffle,
          queue: [...sourceQueue],
          queueIndex: index >= 0 ? index : 0,
        });
      }
    } else {
      set({ shuffle });
    }
  },

  cycleRepeat: () => {
    const order: RepeatMode[] = ["off", "context", "track"];
    const i = order.indexOf(get().repeat);
    const next = order[(i + 1) % order.length] ?? "off";
    set({ repeat: next });
  },

  setVolume: async (volume01) => {
    const volume = Math.min(1, Math.max(0, volume01));
    set({ volume });
    if (sdkPlayer) await runControl(() => sdkPlayer!.setVolume(volume));
  },

  beginSeek: () => set({ seeking: true }),

  previewSeek: (ratio01) => {
    const duration = get().durationMs;
    set({ positionMs: Math.max(0, Math.min(1, ratio01)) * duration });
  },

  commitSeek: async (ratio01) => {
    const duration = get().durationMs;
    const target = Math.max(0, Math.min(1, ratio01)) * duration;
    await runControl(async () => {
      try {
        await ensureReady();
        if (!sdkPlayer) return;
        await sdkPlayer.seek(target);
      } finally {
        set({ seeking: false });
      }
    });
  },

  cancelSeek: () => set({ seeking: false }),
}));
